<script>
import {useI18n} from "vue-i18n";
import Icon from "@/javascript/views/components/Icon.vue";
import DropdownSelect from "@/javascript/views/components/dropdownSelect.vue";
import {useRoute, useRouter} from "vue-router";
import {useUserStore} from "../../stores/user";
import {useDonationStore} from "../../stores/donation";
import {useOrganizationStore} from "../../stores/organizations";
import {useSubscriptionStore} from "../../stores/subscription";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import {useDemoLoopStore} from "../../stores/demoLoop";
import AccordionItem from "../components/accordion/AccordionItem.vue";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import DonationManagement from "./donorProfile/DonationManagement.vue";
import DonorProfile from "./donorProfile/DonorProfile.vue";
import Popup from "../components/popup.vue";
import SubscriptionManagement from "./donorProfile/SubscriptionManagement.vue";
import GiftBasketSideBarButton from "../landing/GiftBasketSidebarButton.vue";
import PageHeader from "../components/landing/PageHeader.vue";
import DashboardHeader from "../components/dashboard/DashboardHeader.vue";
import {useLandingPageStore} from "@/javascript/stores/landingPage";
import RecentActivityTable from "./components/RecentActivityTable.vue";
import BaseModal from "../components/BaseModal.vue";
import { useReportStore} from "@/javascript/stores/report";
import Drawer from "../components/Drawer.vue";
import OrganizationSelector from "../clientDashboard/OrganizationSelector.vue";
import HouseholdManagement from "./donorProfile/HouseholdManagement.vue";
import HouseholdCard from "./donorProfile/HouseholdCard.vue";
import GenerateReport from "./donorProfile/GenerateReport.vue";


export default {
  components: {
    GenerateReport,
    HouseholdCard,
    OrganizationSelector,
    Drawer,
    BaseModal,
    HouseholdManagement,
    DashboardHeader,
    PageHeader,
    GiftBasketSideBarButton,
    SubscriptionManagement,
    Popup,
    DonorProfile,
    DonationManagement,
    AccordionItem,
    Icon,
    DropdownSelect,
    LoadingSpinner,
    RecentActivityTable,
  },
  props: {},
  setup() {
    // Stores
    const userStore = useUserStore();

    const donationStore = useDonationStore();
    const subscriptionStore = useSubscriptionStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();
    const demoLoopStore = useDemoLoopStore();
    const landingPageStore = useLandingPageStore();
    const reportStore = useReportStore();

    // I18n
    const {t} = useI18n();

    // Router
    const router = useRouter();
    const route = useRoute();

    return {
      t,
      router,
      route,
      userStore,
      donationStore,
      subscriptionStore,
      organizationStore,
      analyticEventStore,
      demoLoopStore,
      landingPageStore,
      reportStore
    };
  },
  data() {
    return {
      popupMessage: "",
      currentDonor: null,
      loading: true,
      tabMounted: false,
    };
  },
  computed: {
    activePanel() {
      return this.route.params.panel;
    },
    organizationId() {
      return this.route.params.org;
    },
    organization() {
      return this.landingPageStore.organization;
    },
    brand() {
      return this.landingPageStore.localBrand;
    },
    currentTab() {
      const p = this.route.params.panel;
      if (p === 'tax') return 'tax';
      if (p === 'household') return 'household';
      return 'overview';
    },
    activeTabIndex() {
      if (this.currentTab === 'tax') return 1;
      if (this.currentTab === 'household') return 2;
      return 0;
    },
    indicatorStyle() {
      // Depend on tabMounted to force re-evaluation after mount
      const _ = this.tabMounted;
      const ref = this.$refs[`tab${this.activeTabIndex}`];
      if (ref) {
        // Measure real position and cache it
        const pos = { left: ref.offsetLeft - 4, width: ref.offsetWidth };
        sessionStorage.setItem('tabIndicator', JSON.stringify(pos));
        return {
          transform: `translateX(${pos.left}px)`,
          width: `${pos.width}px`,
          opacity: 1
        };
      }
      // Fallback: read from sessionStorage on initial render
      const cached = JSON.parse(sessionStorage.getItem('tabIndicator') || 'null');
      if (cached) {
        return {
          transform: `translateX(${cached.left}px)`,
          width: `${cached.width}px`,
          opacity: 1
        };
      }
      return { opacity: 0 };
    },
  },
  watch: {
    // Re-hydrate when :org changes in the URL
    'route.params.org': {
      immediate: false,
      async handler() {
        await this.ensureOrgContext();
        this.applyBrandCssVarsSafely();
        await this.loadData();
      },
    },

    // Re-apply CSS vars any time localBrand changes
    'landingPageStore.localBrand': {
      immediate: true,
      deep: true,
      handler() {
        this.applyBrandCssVarsSafely();
      }
    },

    // Set tabMounted after loading finishes and tabs are rendered
    loading(newVal) {
      if (!newVal) {
        this.$nextTick(() => { this.tabMounted = true; });
      }
    }
  },
  mounted() {
    this.screenResize();
    window.addEventListener('resize', this.screenResize);
    this.ensureOrgContext().then(() => this.applyBrandCssVarsSafely());
    this.loadData();

    // Show thank-you message if redirected from a donation
    if (this.route.query.donated) {
      this.showPopup('Thank you for your donation!');
      this.router.replace({ ...this.route, query: {} });
    }
  },
  unmounted() {
    window.removeEventListener("resize", this.screenResize);
  },
  methods: {
    loadData() {
      this.userStore.userId = document.body.dataset.currentUserId;
      this.analyticEventStore.userId = document.body.dataset.currentUserId;

      return this.userStore.loadData(this.organizationId)
        .then(() => {
          if (!this.userStore.userAgreedToAllTerms) {
            this.router.push({ path: '/' });
            return;
          }

          if (this.userStore.organizations.length === 0) {
            return this.userStore.getDonorOrgs(this.organizationId);
          }
        })
        .then(() => {
          const org = this.userStore.organizations.find(o => o.slug === this.organizationId);
          const orgId = org?.id;

          if (orgId) {
            return Promise.all([
              this.userStore.loadUserDonationData(orgId),
              this.userStore.getUserSubscriptions(orgId)
            ]).then(() => {
              this.reportStore.setOrganizationId(this.organizationId);
              return this.userStore.getDonorProfile(orgId);
            }).then((donor) => {
              this.currentDonor = donor;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    ensureOrgContext() {
      const orgSlug = this.route?.params?.org;
      if (!orgSlug) return Promise.resolve();

      const storeOrgMatchesSlug = this.userStore.organizations
        .some(o => o.slug === orgSlug && (
          String(o.id) === String(this.landingPageStore.organizationId) ||
          o.slug === this.landingPageStore.organizationId
        ));

      if (!storeOrgMatchesSlug) {
        this.landingPageStore.setOrganizationId(orgSlug);
        this.landingPageStore.publishedPageLoaded = false;
        this.analyticEventStore.setOrganizationId?.(orgSlug);
      }
      return this.landingPageStore.loadPublishedPage(true);
    },

    applyBrandCssVarsSafely() {
      const b = this.landingPageStore?.localBrand || {};
      if (b.color_primary || b.color_accent) {
        this.landingPageStore.applyCssVarsToRoot?.();
      }
    },
    changePanel(panel) {
      this.$router.push(
        {
          name: "donor-dashboard",
          params: {
            org: this.organizationId,
            panel: panel,
          }
        });
    },

    handleMenuClick(option) {
      switch (option) {
        case 'log_out':
          this.signOut();
          break;
        case 'client_dash':
          this.goToClientDashboard();
          break;
        case 'super_admin':
          this.userStore.goToAdmin();
          break;
      }
    },

    openDemoLoop() {
      this.demoLoopStore.pickerContext = 'donor-dashboard';
      this.demoLoopStore.pickerOpen = true;
    },

    signOut() {
      this.userStore.signOut();
    },

    goToClientDashboard() {
      this.router.push({
        name: 'client-dashboard',
        params: { org: this.organizationId, panel: 'overview' }
      });
    },

    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    },

    screenResize() {
      if (window.innerWidth < 768) {
        this.isSidebarCollapsed = true;
      }
    },
    openDrawer() {
      this.$refs.orgSwitcherDrawer.show();
      this.$refs.orgSwitcherDrawer.toggleFullScreen(true);
    },

    closeDrawer() {
      this.$refs.orgSwitcherDrawer.hide();
    },

    switchOrganization(org) {
      if (this.userStore.currentOrganization?.id === org.id) {
        this.closeDrawer();
        return Promise.resolve();
      }

      this.loading = true;
      this.$refs.orgSwitcherDrawer.hide();
      this.userStore.resetDonationData();
      this.userStore.currentOrganization = org;

      return this.updateStoreIds(org.slug)
        .then(() => this.ensureOrgContext())
        .then(() => {
          this.applyBrandCssVarsSafely();
          this.$router.push({
            name: "donor-dashboard",
            params: { org: org.slug, panel: 'profile' }
          });
          this.loading = false;
        });
    },

    updateStoreIds(organizationId) {
      this.organizationStore.setOrganizationId(organizationId);
      this.donationStore.setOrganizationId(organizationId);
      this.subscriptionStore.setOrganizationId(organizationId);
      this.reportStore.setOrganizationId(organizationId);
      this.reportStore.donors = [];

      return this.userStore.getDonorProfile(organizationId)
        .then((donor) => {
          this.currentDonor = donor;
          this.analyticEventStore.logEvent({
            action: `donor-dashboard-change-organization`
          });
        });
    },
  }
};
</script>

<template>
  <div
    v-if="loading"
    class="flex justify-center items-center h-screen"
  >
    <loading-spinner size="small" />
  </div>

  <div
    v-else
    class="donor-dashboard-container"
  >
    <dashboard-header
      title="Donor Dashboard"
      fallback="name"
      :ignore-org="false"
      @menu-click="handleMenuClick"
    >
      <template #nav-menu-items>
        <nav class="nav-menu">
          <div
            v-if="userStore.userIsOrganizationMember"
            class="nav-item flex flex-row justify-between"
            @click="handleMenuClick('client_dash')"
          >
            <div>Client Dashboard</div>
          </div>

          <div
            v-if="userStore.organizations.length > 1"
            class="nav-item flex flex-row justify-between"
            @click="openDrawer"
          >
            <div>Switch Organization</div>
            <icon
              icon="sitemap"
              color="#4f4f4f"
            />
          </div>

          <div
            v-if="userStore.userIsAdmin"
            class="nav-item flex flex-row justify-between"
            @click="handleMenuClick('super_admin')"
          >
            <div>Backend Admin</div>
          </div>
          <div
            class="nav-item flex flex-row justify-between"
            @click="openDemoLoop"
          >
            <div>Demo Tour</div>
            <icon
              icon="play"
              color="#4f4f4f"
            />
          </div>
          <div
            class="nav-item flex flex-row justify-between"
            @click="handleMenuClick('log_out')"
          >
            <div>Sign Out</div>
            <icon
              icon="logout"
              color="#4f4f4f"
            />
          </div>
        </nav>
      </template>
    </dashboard-header>

    <!-- Page background + top flourish -->
    <div
      class="page-bg pointer-events-none"
      aria-hidden="true"
    />

    <!-- Tabs -->
    <div class="mx-auto w-full max-w px-4 sm:px-6 lg:px-8 mt-4">
      <div class="segmented" :class="{ 'is-ready': tabMounted }">
        <div class="segmented__indicator" :style="indicatorStyle" />
        <button
          ref="tab0"
          class="segmented__item"
          :class="{ 'is-active': currentTab === 'overview' }"
          @click="changePanel('overview')"
        >
          Overview
        </button>
        <button
          ref="tab1"
          class="segmented__item"
          :class="{ 'is-active': currentTab === 'tax' }"
          @click="changePanel('tax')"
        >
          Gifts
        </button>
        <button
          ref="tab2"
          class="segmented__item"
          :class="{ 'is-active': currentTab === 'household' }"
          @click="changePanel('household')"
        >
          Household
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="dashboard-panel-content">
      <div class="mx-auto w-full max-w px-4 sm:px-6 lg:px-8 py-6">
        <transition name="tab-fade" mode="out-in">
          <!-- OVERVIEW -->
          <div v-if="currentTab === 'overview'" key="overview">
            <div class="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr,2fr,1fr,1fr] items-stretch mb-8">

          <!--   Cards    -->
              <donor-profile />

              <subscription-management />

              <generate-report :loading="loading" :current-donor="currentDonor" @show-popup="showPopup" />

              <household-card @change-panel="changePanel" />

            </div>
            <donation-management />
          </div>

          <!-- GIFTS -->
          <div v-else-if="currentTab === 'tax'" key="tax">
            <div class="p-0">
              <donation-management :paginate="true" />
            </div>
          </div>

          <!-- HOUSEHOLD Tab-->
          <div v-else-if="currentTab === 'household'" key="household">
            <household-management />
          </div>
        </transition>
      </div>
    </div>

  </div>

  <drawer
    ref="orgSwitcherDrawer"
    size="large"
    hide-close-button
  >
    <organization-selector
      @organization-selected="switchOrganization"
      @close="closeDrawer"
    />
  </drawer>


  <popup
    ref="popup"
    :message="popupMessage"
    :duration="1400"
  />

</template>


<style lang="scss">
@import '@/styles/_variables.scss';

// Tab transition - If we want a page slide between tabs
//.tab-fade-enter-active, .tab-fade-leave-active {
//  transition: opacity 0.3s ease, transform 0.3s ease;
//}
//.tab-fade-enter-from {
//  opacity: 0;
//  transform: translateX(25px);
//}
//.tab-fade-leave-to {
//  opacity: 0;
//  transform: translateX(-25px);
//}

// Global button styles (for drawers/modals that render outside .donor-dashboard-container)
.btn-brand {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: var(--brand-primary, #10be5a);
  color: var(--brand-primary-text, #ffffff);
  font-weight: 700;
  font-size: 0.7rem;
  box-shadow: 0 1px 2px rgba(16,190,90,.25);
  transition: filter .15s ease;
}
.btn-brand:hover { filter: brightness(0.97); }

.btn-ghost {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(100,116,139,.3);
  background: #ffffff;
  color: #0f172a;
  font-weight: 700;
  font-size: 0.7rem;
  transition: background .15s ease;
}
.btn-ghost:hover { background: #f1f5f9; }

.donor-dashboard-container {
  min-height: 100vh;
  position: relative;
  background: #f6f7f8;

  .page-bg {
    position: absolute;
    inset: 0 auto auto 50%;
    width: 1200px;
    height: 280px;
    transform: translateX(-50%);
    background:
      radial-gradient(40% 60% at 50% 0%,
        color-mix(in oklab, var(--brand-primary, #10be5a) 20%, white 80%) 0%,
        transparent 65%)
      ,
      radial-gradient(30% 50% at 30% 0%,
        color-mix(in oklab, var(--brand-accent, #3a4473) 16%, white 84%) 0%,
        transparent 70%);
    opacity: .35;
    filter: blur(30px);
    pointer-events: none;
  }

  /* Content wrapper */
  .dashboard-panel-content {
    overflow-x: hidden;
    overflow-y: visible;
  }

  /* Segmented tabs */
  .segmented {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    background: #eef1f4;
    border: 1px solid rgba(100,116,139,.18);
    border-radius: 999px;
    position: relative;
  }

  .segmented__indicator {
    position: absolute;
    top: 4px;
    bottom: 4px;
    border-radius: 999px;
    background: var(--brand-primary, #10be5a);
    box-shadow: 0 2px 4px color-mix(in srgb, var(--brand-primary, #10be5a) 30%, transparent);
    transition: transform 0.3s ease, width 0.3s ease, opacity 0.3s ease;
    z-index: 0;
  }

  .segmented__item {
    position: relative;
    z-index: 1;
    padding: 8px 14px;
    font-weight: 600;
    font-size: 0.9rem;
    color: #475569;
    border-radius: 999px;
    transition: color .3s ease;
  }

  .segmented__item:hover {
    color: #1e293b;
  }

  .segmented__item.is-active {
    color: #475569;
  }

  .segmented.is-ready .segmented__item.is-active {
    color: var(--brand-primary-text, #475569);
  }

  .pill {
    @apply rounded-2xl py-2 px-3 flex items-center justify-center;
    @apply rounded-full py-1 px-2 whitespace-nowrap;
  }

  .subscription-interval-pill {
    @apply text-xs font-bold bg-default_secondary text-white flex-row;
    background: var(--brand-accent, #3a4473);
    color: var(--brand-accent-text, #EBEBEB);
    border: var(--brand-accent-border, 1px solid transparent);
  }

  .subscription-status-pill {
    @apply text-xs font-bold bg-default_secondary text-white flex-row;
    text-transform: capitalize;
    color: var(--brand-primary-text, #EBEBEB);
    border: var(--brand-primary-border, 1px solid transparent);

    &.status--active { background: var(--brand-primary, #10be5a); }
    &.status--pending, &.status--paused, &.status--trialing { @apply bg-gray-500; }
    &.status--cancelled { @apply bg-red-800; }
  }

  .surface :deep(.data-card) {
    --card-padding: 12px;
  }
}


.donor-dashboard-container {
  min-height: 100vh;
  overflow-x: hidden;

  // Main content w/ background
  .dashboard-panel-content {
    overflow-x: hidden;
    overflow-y: visible;

    @screen md {
      background-size: 50dvh auto;
    }
  }

  .admin-sidebar-container {
    @apply h-full max-h-screen relative transition-all duration-300 z-10 border-r;
    &.collapsed {
      width: 60px;

    }

    &.expanded {
      @apply w-64;
    }
  }

  // Sidebar
  .admin-sidebar {
    @apply shadow-lg;
    @apply pt-2 pb-4 px-0;
    @apply flex flex-col;
    @apply h-full max-h-screen transition-all duration-300 overflow-y-visible;
    overflow-x: hidden;

    &.expanded {
      @apply px-2;
    }

    .sidebar-menu-items-list {

      .sidebar-item {
        @apply flex items-center px-4 py-2 mb-2;
        @apply text-slate-600 cursor-pointer;
        @apply md:rounded;

        .icon {
          color: var(--brand-primary);
        }

        /* make the svg obey the element’s color */
        .icon svg,
        .icon svg path {
          fill: currentColor;
        }

        &.selected {
          background: var(--brand-primary);
          color: var(--brand-primary-text);

          .icon svg path {
            @apply fill-default_white;
          }
        }

        &:hover {
          background: var(--brand-primary);
          color: var(--brand-primary-text);

          .icon svg path {
            @apply fill-default_white;
          }
        }


      }

      &.collapsed {
        .sidebar-item {
          @apply mx-1;
        }
      }

      .sidebar-divider {
        @apply bg-slate-400;
        @apply my-4;
        width: 100%;
        height: 1px;
      }
    }

    .panel-label {
      @apply pl-4 hidden md:block font-bold;
      @apply font-bold transition-all duration-200;

      &.expanded {
        @apply pl-4;
        @screen md {
          @apply inline-block;
        }
      }

      &.collapsed {
        @apply opacity-0 w-0 overflow-hidden;
      }
    }

    &.collapsed {
      width: 60px;
      padding-left: 0;
      padding-right: 0;

      li {
        padding-left: 0;
        padding-right: 0;
        margin-left: auto;
        margin-right: auto;
        justify-content: center;
      }

      .sidebar-item {
        padding-left: 0;
        padding-right: 0;
        justify-content: center;
        @apply rounded-md;
      }

      .panel-label {
        display: none !important; // Hide text labels fully
      }
    }
  }

  .pill {
    @apply rounded-2xl py-2 px-3 flex items-center justify-center;
    @apply rounded-full py-1 px-2 whitespace-nowrap;

  }

  .subscription-interval-pill {
    @apply text-xs font-bold bg-default_secondary text-white flex-row;
    background: var(--brand-accent, #3a4473);
    color: var(--brand-accent-text, #EBEBEB);
    border: var(--brand-accent-border, 1px solid transparent);
  }

  .subscription-status-pill {
    @apply text-xs font-bold bg-default_secondary  text-white flex-row;
    text-transform: capitalize;
    color: var(--brand-primary-text, #EBEBEB);
    border: var(--brand-primary-border, 1px solid transparent);

    &.status--active {
      background: var(--brand-primary, #10be5a);
    }

    &.status--pending, &.status--paused, &.status--trialing {
      @apply bg-gray-500;
    }

    &.status--cancelled {
      @apply bg-red-800;
    }
  }

  /* Contribution Reports inner CTA */
  .contrib-cta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px;
    background: #f7fafc; /* subtle panel bg */
    border: 1px dashed rgba(100,116,139,.35);
    border-radius: 12px;

    @media (max-width: 640px) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .contrib-cta__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .contrib-cta__icon {
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    display: grid;
    place-items: center;
    background: color-mix(in oklab, var(--brand-accent, #3a4473) 10%, #ffffff 90%);
    border: 1px solid rgba(100,116,139,.25);
  }

  .contrib-cta__title {
    font-weight: 700;
    font-size: 0.95rem;
    color: #0f172a;
  }

  .contrib-cta__subtitle {
    font-size: 0.82rem;
    color: #64748b;
    margin-top: 2px;
  }

  /* Buttons */
  .contrib-cta__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    @media (max-width: 640px) {
      justify-content: stretch;

      .btn-brand, .btn-ghost { width: 100%; }
    }
  }

  .btn-brand {
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: var(--brand-primary, #10be5a);
    color: var(--brand-primary-text, #ffffff);
    font-weight: 700;
    font-size: 0.7rem;
    box-shadow: 0 1px 2px rgba(16,190,90,.25);
    transition: filter .15s ease;
  }
  .btn-brand:hover { filter: brightness(0.97); }

  .btn-ghost {
    padding: 8px 14px;
    border-radius: 8px;
    border: 1px solid rgba(100,116,139,.3);
    background: #ffffff;
    color: #0f172a;
    font-weight: 700;
    font-size: 0.7rem;
    transition: background .15s ease;
  }
  .btn-ghost:hover { background: #f1f5f9; }

  .year-select {
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid rgba(100,116,139,.3);
    background: #ffffff;
    color: #0f172a;
    font-weight: 600;
    font-size: 0.8rem;
  }


}
</style>
